import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { AuthStore, TyAccount, TyAuthStoreAccess, TyClient } from "./types";

/** The subset written to localStorage — `isAddingAccount` is deliberately session-only. */
type TyPersistedAuth = { token: string | null; client: TyClient | null; accounts: TyAccount[] };

/**
 * Several people share one device in a café, and one person often holds more than one account.
 * Every signed-in identity is therefore kept side by side and switched between, so signing in with
 * an OTP happens once per account instead of once per shift.
 */
const useAuth = create<AuthStore>()(
    persist(
        (set, get) => ({
            token: null,
            client: null,
            accounts: [],
            isAddingAccount: false,

            setAuth: (token, client) =>
                set((state) => {
                    const others = state.accounts.filter((a) => a.client._id !== client._id);
                    return { token, client, accounts: [...others, { token, client }], isAddingAccount: false };
                }),

            updateClient: (client) =>
                set((state) => ({
                    client,
                    accounts: state.accounts.map((a) => (a.client._id === client._id ? { ...a, client } : a)),
                })),

            updateStoreAccess: (storeId: string, storeAccess: TyAuthStoreAccess) => {
                const client = get().client;
                if (!client) return;
                const updated = client.storeAccess.filter((a) => a.store !== storeId);
                updated.push({ ...storeAccess, store: storeId });
                const next: TyClient = { ...client, storeAccess: updated };
                set((state) => ({
                    client: next,
                    accounts: state.accounts.map((a) => (a.client._id === next._id ? { ...a, client: next } : a)),
                }));
            },

            /**
             * Rebuilds `owner` and `storeAccess` from `/auth/stores`, which is the authoritative list of
             * what this client can reach.
             *
             * Without it, membership granted anywhere other than this device's own "create store" flow
             * stays invisible until the next sign-in — an admin adding an owner, or a seeded store — and
             * the dashboard shows only the ungated tabs while the API happily allows the writes.
             */
            syncMembership: (stores) => {
                const client = get().client;
                if (!client) return;

                const owner = stores.filter((store) => store.isOwner).map((store) => store._id);
                const storeAccess: TyAuthStoreAccess[] = stores
                    .filter((store) => !store.isOwner && store.storeAccess)
                    .map((store) => ({
                        store: store._id,
                        permissions: store.storeAccess!.permissions,
                        // The API expands branch ids into `{ _id, name }` for display, but every
                        // permission check compares plain ids — keeping the objects would quietly
                        // fail every branch-scoped check for staff.
                        branches: store.storeAccess!.branches === "all" ? "all" : store.storeAccess!.branches.map((branch) => branch._id),
                    }));

                // Setting unconditionally would re-render, re-fetch and set again, forever.
                const unchanged = JSON.stringify({ owner, storeAccess }) === JSON.stringify({ owner: client.owner, storeAccess: client.storeAccess });
                if (unchanged) return;

                const next: TyClient = { ...client, owner, storeAccess };
                set((state) => ({
                    client: next,
                    accounts: state.accounts.map((a) => (a.client._id === next._id ? { ...a, client: next } : a)),
                }));
            },

            switchAccount: (clientId) => {
                const target = get().accounts.find((a) => a.client._id === clientId);
                if (!target) return;
                set({ token: target.token, client: target.client, isAddingAccount: false });
            },

            /** Signs one identity out and promotes another, so the device rarely lands on /login. */
            removeAccount: (clientId) =>
                set((state) => {
                    const remaining = state.accounts.filter((a) => a.client._id !== clientId);
                    const wasActive = state.client?._id === clientId;
                    if (!wasActive) return { accounts: remaining };
                    const next: TyAccount | undefined = remaining[0];
                    return {
                        accounts: remaining,
                        token: next?.token ?? null,
                        client: next?.client ?? null,
                    };
                }),

            startAddingAccount: () => set({ isAddingAccount: true }),
            stopAddingAccount: () => set({ isAddingAccount: false }),

            clearAuth: () => set({ token: null, client: null, accounts: [], isAddingAccount: false }),

            /**
             * The server no longer recognises the active token — the session expired, was revoked, or
             * the client it belonged to has been deleted. Whatever the cause, that identity cannot be
             * used again, so it is removed from the device rather than left to fail every request.
             *
             * A device may hold several signed-in accounts; only the dead one goes, and the next takes
             * over. With none left there is nothing to show behind the dashboard, so the page is
             * replaced rather than routed — a hard load drops every screen still holding data this
             * session fetched, which a client-side navigation would leave in memory.
             */
            dropDeadSession: () => {
                const { client, accounts } = get();
                const remaining = accounts.filter((a) => a.client._id !== client?._id);
                const next = remaining[0];

                set({ accounts: remaining, token: next?.token ?? null, client: next?.client ?? null, isAddingAccount: false });

                if (!next && typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
                    window.location.replace("/login");
                }
            },
        }),
        {
            name: "auth",
            partialize: (state) => ({
                token: state.token,
                client: state.client,
                accounts: state.accounts,
            }),
            /** Devices signed in before multi-account shipped carry a token but no accounts list. */
            migrate: (persisted) => {
                const state = (persisted ?? {}) as Partial<TyPersistedAuth>;
                const token = state.token ?? null;
                const client = state.client ?? null;
                const accounts = state.accounts?.length ? state.accounts : token && client ? [{ token, client }] : [];
                return { token, client, accounts };
            },
            version: 1,
        }
    )
);

export default useAuth;
